/**
 * AdminLayout -- Full Admin Shell Layout
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 *
 * Renders the complete admin application shell with:
 * - Skip navigation link inside the sidebar nav (WCAG 2.4.1)
 * - ARIA live region for route announcements
 * - Sidebar navigation (role="complementary" + role="navigation")
 * - Header (role="banner") with notification bell
 * - Content area hosting page components (pages provide their own <main>)
 * - RouteErrorBoundary isolating route errors from the shell
 * - Focus management on route changes
 * - Notification bell with keyboard support (Escape to close)
 */

import React, { useEffect, useMemo, useState, useCallback, memo, lazy, Suspense } from 'react';
import { Outlet, useLocation, useNavigate, Link, Navigate } from 'react-router-dom';
import { PageSpinner } from '../components/PageSpinner';
import { RouteErrorBoundary } from '../components/RouteErrorBoundary';
import { LiveAnnouncer } from '../components/LiveAnnouncer';
import { FocusManager } from '../components/FocusManager';
import { NotificationBell } from '../components/NotificationBell';
import { useAuthStore } from '../stores/authStore';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';
import { VersionSelector } from '../components/VersionSelector';
import { resolveModuleNavigation } from './moduleNavigation';
import type { NavItem, Notification } from '../types';
import {
  LayoutDashboard,
  GitBranch,
  Activity,
  BookOpen,
  Gauge,
  HelpCircle,
  Grid3X3,
  FileText,
  ShieldCheck,
  Building2,
  ClipboardList,
  FilePlus,
  Users,
  Settings,
  Layers,
  LogOut,
  PanelLeftClose,
  PanelRightClose,
  Search,
  AlertTriangle,
  RefreshCw,
  BarChart3,
} from "lucide-react";

const GlobalSearch = lazy(() => import('../components/GlobalSearch'));
import type { LucideIcon } from "lucide-react";

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard,
  GitBranch,
  Activity,
  BookOpen,
  Gauge,
  HelpCircle,
  Grid3X3,
  FileText,
  ShieldCheck,
  Building2,
  ClipboardList,
  FilePlus,
  Users,
  Settings,
  Layers,
};

/**
 * Resolve the sidebar module label + nav items for the current route and user.
 * The pure resolution -- including the multi-role "Switch module" switcher (N2)
 * -- lives in ./moduleNavigation so it can be unit-tested without the shell.
 */
function useModuleNavigation(): { moduleLabel: string; navItems: NavItem[] } {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  return resolveModuleNavigation(location.pathname, user);
}

export function SidebarNavItem({ item, activeHref }: { item: NavItem; activeHref: string }) {
  // Auto-expand group when active route matches a child href, or when the item
  // opts in via defaultExpanded (e.g. the multi-role "Switch module" switcher,
  // whose targets are other modules' homes and so never match the active route).
  const childMatchesActive = item.group && item.children?.some((c) => c.href === activeHref);
  const [expanded, setExpanded] = useState(childMatchesActive || item.defaultExpanded || false);
  // Exact match for module home routes (e.g. /content-studio, /test-admin/clients), startsWith for others
  const isModuleHome = item.href === '/content-studio' || item.href === '/client' || item.href === '/super-admin' || item.href === '/test-admin/tests';
  const isActive = item.href
    ? isModuleHome
      ? activeHref === item.href
      : activeHref === item.href || activeHref.startsWith(item.href + '/')
    : false;

  if (item.group && item.children) {
    return (
      <li>
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/[0.08]"
          aria-expanded={expanded}
        >
          <span className="flex items-center gap-3">
            {item.icon &&
              ICON_MAP[item.icon] &&
              React.createElement(ICON_MAP[item.icon], { size: 20, "aria-hidden": true })}
            {item.label}
          </span>
          <span aria-hidden="true">{expanded ? "\u25B2" : "\u25BC"}</span>
        </button>
        {expanded && (
          <ul className="ml-4 mt-1 space-y-1">
            {item.children.map((child) => (
              <li key={child.href ?? child.label}>
                <Link
                  to={child.href ?? "#"}
                  className="block px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/[0.08]"
                >
                  {child.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  return (
    <li>
      <Link
        to={item.href ?? "#"}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
          isActive ? "bg-white/[0.08] text-white font-medium" : "text-white/80 hover:bg-white/[0.08]"
        }`}
      >
        {item.icon &&
          ICON_MAP[item.icon] &&
          React.createElement(ICON_MAP[item.icon], { size: 20, "aria-hidden": true })}
        {item.label}
      </Link>
    </li>
  );
}

/** Format a relative time string from an ISO date */
function timeAgo(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Notification type styling */
const NOTIF_TYPE_CONFIG: Record<string, { icon: React.ElementType; bg: string; color: string }> = {
  action_required: { icon: AlertTriangle, bg: 'bg-amber-100', color: 'text-amber-600' },
  status_change: { icon: RefreshCw, bg: 'bg-blue-100', color: 'text-blue-600' },
  results_available: { icon: BarChart3, bg: 'bg-green-100', color: 'text-green-600' },
};
const NOTIF_TYPE_DEFAULT = { icon: AlertTriangle, bg: 'bg-brand-steel/15', color: 'text-brand-steel' };

/** Map entity type to a route path for notification navigation */
function notificationRoute(n: Notification, roles: string[] = []): string {
  const raw = n as unknown as Record<string, unknown>;
  const entityType = raw.related_entity_type as string | undefined;
  const entityId = raw.related_entity_id as string | undefined;
  if (entityType === "Version" && entityId) return `/content-studio/versions/${entityId}`;
  if (entityType === "Test" && entityId) {
    if (roles.includes("Client Manager")) return `/client/tests/${entityId}`;
    return `/test-admin/tests/${entityId}/monitor`;
  }
  if (entityType === "activity" && entityId) return "/content-studio/activities";
  return "/";
}

/** Memoized sidebar to prevent re-renders from main content state changes */
const MemoizedSidebar = memo(function MemoizedSidebar({
  moduleLabel,
  navItems,
  activeHref,
  userName,
  userEmail,
  onSignOut,
  onSkipClick,
  onSearchClick,
}: {
  moduleLabel: string;
  navItems: NavItem[];
  activeHref: string;
  userName: string;
  userEmail: string;
  onSignOut: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onSkipClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onSearchClick?: () => void;
}) {
  const initials = userName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <aside role="complementary" className="flex flex-col h-full w-64 bg-brand-navy" style={{ width: "16rem" }}>
      <div className="px-4 py-5 border-b border-white/10">
        <div className="flex items-center gap-2.5 border-b border-slate-800">
          <div className="w-8 h-8 rounded-lg bg-brand-crimson flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5"></path>
              <path d="M2 12l10 5 10-5"></path>
            </svg>
          </div>
          <span className="text-lg font-bold text-white tracking-tight">XYDEN</span>
        </div>
        {/* <span className="text-lg font-bold text-white tracking-tight">XYDEN</span>
        <p className="text-xs text-white/50 mt-0.5">{moduleLabel}</p> */}
      </div>
      <div className="px-5 pt-5 pb-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{moduleLabel}</p>
      </div>
      {onSearchClick && (
        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={onSearchClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/8 text-white/50 text-sm hover:bg-white/12 transition-colors cursor-pointer"
            aria-label="Search everything"
          >
            <Search size={16} aria-hidden="true" />
            <span className="flex-1 text-left">Search everything...</span>
          </button>
        </div>
      )}
      <nav role="navigation" aria-label="Main navigation" className="flex-1 px-3 py-4 overflow-y-auto">
        {/* Skip to main content link -- first focusable in nav */}
        <a
          href="#main-content"
          onClick={onSkipClick}
          className="sr-only focus:not-sr-only focus:block focus:px-3 focus:py-2 focus:mb-2 focus:bg-brand-crimson focus:text-white focus:rounded-lg focus:text-sm focus:font-medium"
        >
          Skip to main content
        </a>
        <ul className="space-y-1">
          {navItems.map((item) => (
            <SidebarNavItem key={item.href ?? item.label} item={item} activeHref={activeHref} />
          ))}
        </ul>
      </nav>
      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-semibold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-200 truncate">{userName}</p>
            <p className="text-xs text-slate-500 truncate">{userEmail}</p>
          </div>
          <a href="/login" onClick={onSignOut} className="text-slate-500 hover:text-slate-300 transition-colors" aria-label="Sign out">
            <LogOut size={16} aria-hidden="true" />
          </a>
        </div>
      </div>
    </aside>
  );
});

/** Memoized TopBar using 6F NotificationBell with Link-based renderItem */
const MemoizedTopBar = memo(function MemoizedTopBar({
  sidebarCollapsed,
  onToggleSidebar,
  notifications,
  onMarkRead,
  onMarkAllRead,
  firstUnreadId,
  versionSelector,
  userRoles,
}: {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  notifications: Notification[];
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  firstUnreadId: string | null;
  versionSelector?: React.ReactNode;
  userRoles: string[];
}) {
  const renderNotificationItem = useCallback(
    (notification: Notification, markRead: (id: string) => void) => {
      const raw = notification as unknown as Record<string, unknown>;
      const nType = raw.type as string | undefined;
      const created = raw.created_at as string | undefined;
      const config = (nType && NOTIF_TYPE_CONFIG[nType]) || NOTIF_TYPE_DEFAULT;
      const TypeIcon = config.icon;

      return (
        <Link
          to={notificationRoute(notification, userRoles)}
          onClick={() => markRead(notification.id)}
          className={`flex items-start gap-3 px-4 py-3 hover:bg-brand-panel/50 transition-colors ${!notification.read ? 'bg-blue-50/60' : ''}`}
        >
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${config.bg} shrink-0 mt-0.5`}>
            <TypeIcon size={16} className={config.color} aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-gray-900 line-clamp-1">{notification.title}</p>
              {!notification.read && (
                <span className="w-2 h-2 rounded-full bg-brand-crimson shrink-0 mt-1.5" aria-label="Unread" />
              )}
            </div>
            <p className="text-xs text-brand-steel mt-0.5 line-clamp-2">{notification.message}</p>
            {created && (
              <p className="text-[11px] text-brand-steel/60 mt-1">{timeAgo(created)}</p>
            )}
          </div>
        </Link>
      );
    },
    [firstUnreadId, userRoles]
  );

  return (
    <header
      role="banner"
      className="flex items-center justify-between px-6 py-3 bg-white border-b border-brand-steel/50 shadow-sm"
    >
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onToggleSidebar}
          className={`p-2 rounded-lg transition-colors ${sidebarCollapsed ? "bg-brand-navy text-white hover:bg-brand-navy/90" : "bg-brand-panel text-brand-steel hover:text-gray-700 hover:bg-brand-steel/15"}`}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? <PanelRightClose size={20} /> : <PanelLeftClose size={20} />}
        </button>
        {versionSelector}
      </div>
      <div className="flex items-center gap-3 relative">
        <NotificationBell
          notifications={notifications}
          onMarkRead={onMarkRead}
          onMarkAllRead={onMarkAllRead}
          renderItem={renderNotificationItem}
          markAllLabel="Clear all notifications"
        />
      </div>
    </header>
  );
});

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthResolved } = useAuthStore();
  const { moduleLabel, navItems } = useModuleNavigation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);

  const isContentStudio = location.pathname.startsWith("/content-studio");
  const handleSearchClick = useCallback(() => setSearchOpen(true), []);
  const handleToggleSidebar = useCallback(() => setSidebarCollapsed((prev) => !prev), []);

  useEffect(() => {
    let cancelled = false;

    const fetchNotifications = () => {
      apiClient.get<Notification[]>('/api/notifications')
        .then(data => { if (!cancelled) setNotifications(data); })
        .catch(() => { /* Panel isolation: notification failure does not crash */ });
    };

    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 30000);

    return () => { cancelled = true; clearInterval(intervalId); };
  }, []);

  const handleMarkRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    apiClient.patch(`/api/notifications/${id}`, { read: true }).catch(() => {});
  }, []);

  const handleMarkAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    apiClient.put("/api/notifications/read-all").catch(() => {});
  }, []);

  const handleSignOut = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      authService.logout().catch(() => {}).finally(() => {
        logout();
        navigate("/login");
      });
    },
    [logout, navigate]
  );

  const handleSkipClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const main = document.querySelector("main");
    if (main instanceof HTMLElement) {
      if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
      main.focus();
    }
  }, []);

  // Sort newest-first by created_at so the most recent notification sits at
  // the top of the popup. The /api/notifications response is in fixture/insertion
  // order, so without this the user sees oldest-first which is the wrong UX
  // and also out-of-order when fixture entries aren't strictly chronological.
  // Hook must be called before the early returns below to satisfy Rules of Hooks.
  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [notifications]);

  if (!isAuthResolved) {
    return <PageSpinner label="Loading" />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const defaultUser = user ?? { id: "", name: "User", email: "", roles: [] as string[] };
  const firstUnreadId = sortedNotifications.find((n) => !n.read)?.id ?? null;

  return (
    <>
      <LiveAnnouncer />
      <div className="flex h-screen bg-brand-panel overflow-hidden print:block print:overflow-visible print:h-auto">
        <div
          className="shrink-0 transition-all duration-300 ease-in-out overflow-hidden print:hidden"
          style={{ width: sidebarCollapsed ? 0 : "16rem" }}
        >
          <MemoizedSidebar
            moduleLabel={moduleLabel}
            navItems={navItems}
            activeHref={location.pathname}
            userName={defaultUser.name}
            userEmail={defaultUser.email}
            onSignOut={handleSignOut}
            onSkipClick={handleSkipClick}
            onSearchClick={isContentStudio ? handleSearchClick : undefined}
          />
        </div>

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden print:block print:overflow-visible print:h-auto">
          <MemoizedTopBar
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={handleToggleSidebar}
            notifications={sortedNotifications}
            onMarkRead={handleMarkRead}
            onMarkAllRead={handleMarkAllRead}
            firstUnreadId={firstUnreadId}
            versionSelector={location.pathname.startsWith('/content-studio') ? <VersionSelector /> : undefined}
            userRoles={defaultUser.roles}
          />
          <div id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto print:overflow-visible print:h-auto">
            <FocusManager />
            <RouteErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </div>
      </div>
      {isContentStudio && (
        <Suspense fallback={null}>
          <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        </Suspense>
      )}
    </>
  );
}

export default AdminLayout;
